import { WebGLRenderer, SRGBColorSpace, Cache, ShaderChunk } from 'three'

import Time from '../../utils/performance/TimeManager.js'
import assetsPlugin from '../plugins/assets/assetsPlugin.js'
import loadKTX2 from '../plugins/assets/loadKTX2.js'
import baluchon from 'virtual:baluchon'

/// #if __DEBUG__
window.__BALUCHON__ = baluchon
/// #endif

export const webgl = {
    $renderer: null,
    $time: null,
    $scenes: {},
    $assets: null
}

export function createWebgl(config) {
    return config
}

export function initWebgl(config) {
    Cache.enabled = true

    if (config.shaderChunks) {
        for (const [path, mod] of Object.entries(config.shaderChunks)) {
            const id = path.split('/').pop().slice(0, -5)
            ShaderChunk[id] = mod.default ?? mod
        }
    }

    webgl.$time = {
        get elapsed() { return Time.elapsed },
        get clampedDt() { return Time.clampedDt },
        get dt() { return Time.dt },
        clampTo60fps: true,
        start() { Time.start() }
    }

    webgl.$renderer = createRendererWrapper()

    assetsPlugin(webgl)
    webgl.$assets.setManifest(baluchon)

    config.setup?.()

    if (webgl.$renderer.instance) loadKTX2.detectSupport(webgl.$renderer.instance)

    /// #if __DEBUG__
    if (window.DEV?.gui && !window.DEV.gui.disabled) {
        window.DEV.gui.createWebGLGUI(webgl.$renderer.instance)
    }
    /// #endif

    return {
        async preload() {
            await config.preload?.()
        },

        start() {
            config.start?.()
        },

        update() {
            config.update?.()
        },

        render() {
            config.render?.()
        },

        resize() {
            webgl.$renderer.resize()
            for (const key in webgl.$scenes) {
                webgl.$scenes[key]?.resize?.()
            }
        },

        pointermove(e) {
            for (const key in webgl.$scenes) {
                webgl.$scenes[key]?.pointermove?.(e)
            }
        },

        setQualityManager(qm) {
            const BASE_RATIOS = [0.5, 0.75, 1.0, 1.5, 2.0, 2.0]

            const getRatio = (q) => {
                const maxRatio = Math.min(window.devicePixelRatio, 2)
                return Math.min(BASE_RATIOS[Math.max(0, Math.min(5, q | 0))], maxRatio)
            }

            qm.onQualityChange = (q) => webgl.$renderer.setPixelRatio(getRatio(q))
            webgl.$renderer.setPixelRatio(getRatio(qm.quality))
            config.setQualityManager?.(qm)
        }
    }
}


function createRendererWrapper() {
    let _renderer = null
    const resizeCallbacks = []

    function emitResize() {
        const w = _renderer.domElement.width
        const h = _renderer.domElement.height
        for (let i = 0; i < resizeCallbacks.length; i++) resizeCallbacks[i](w, h)
    }

    return {
        get instance() { return _renderer },
        get drawingBufferWidth() { return _renderer ? _renderer.domElement.width : 0 },
        get drawingBufferHeight() { return _renderer ? _renderer.domElement.height : 0 },

        setup(options = {}) {
            const canvas = document.getElementById('webgl')

            _renderer = new WebGLRenderer({
                canvas,
                powerPreference: 'high-performance',
                ...options
            })

            _renderer.outputColorSpace = SRGBColorSpace
            _renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
            _renderer.setSize(window.innerWidth, window.innerHeight)
            emitResize()
        },

        resize() {
            if (!_renderer) return
            _renderer.setSize(window.innerWidth, window.innerHeight)
            emitResize()
        },

        setPixelRatio(ratio) {
            if (!_renderer) return
            _renderer.setPixelRatio(ratio)
        },

        onResize(fn) {
            resizeCallbacks.push(fn)
            return () => {
                const i = resizeCallbacks.indexOf(fn)
                if (~i) resizeCallbacks.splice(i, 1)
            }
        }
    }
}
